import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, Mail, Lock, AlertCircle, Clock, ShieldAlert, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import api, { sessionManager } from '@/lib/api';

export default function Login({ onLogin }) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');
  const [passwordWarning, setPasswordWarning] = useState(null);

  // Check for session-related messages in URL
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'timeout') {
      setSessionMessage('Your session has expired due to inactivity. Please log in again.');
      toast.warning('Session expired due to inactivity');
    } else if (reason === 'expired') {
      setSessionMessage('Your session has expired. Please log in again.');
      toast.warning('Session expired');
    } else if (reason === 'locked') {
      setSessionMessage('Your account has been temporarily locked. Please try again later.');
      toast.error('Account locked');
    } else if (reason === 'password_expired') {
      setSessionMessage('Your password has expired. Please change it after logging in.');
      toast.warning('Password expired');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionMessage('');
    setPasswordWarning(null);
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user, hospital, security_status } = response.data;
      
      // Start session monitoring after successful login
      sessionManager.start();
      
      // Check for password expiration warning
      if (security_status?.password_status?.requires_change) {
        // Password expired - user must change it
        toast.warning(security_status.password_status.reason || 'Please change your password.');
        // Store in localStorage so SecuritySettings can show it
        localStorage.setItem('password_change_required', 'true');
      } else if (security_status?.password_status?.warning) {
        // Password expiring soon - show warning
        toast.info(`Your password will expire in ${security_status.password_status.days_remaining} days.`);
      }
      
      onLogin(user, access_token, hospital);
      toast.success('Login successful!');
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      
      if (status === 429) {
        // Rate limited
        setError(message);
        toast.error('Too many attempts. Please wait before trying again.');
      } else if (status === 423) {
        // Account locked
        setError(message);
        toast.error('Account temporarily locked');
      } else {
        setError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ 
      background: 'linear-gradient(135deg, #4A238F 0%, #3A1B72 100%)'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-lg shadow-xl p-8 border border-slate-200">
          {/* Session Message Banner */}
          {sessionMessage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
            >
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{sessionMessage}</p>
            </motion.div>
          )}
          
          {/* Logo */}
          <div className="flex flex-col items-center justify-center mb-8">
            <img 
              src="/logo-complete.png" 
              alt="Zesta Healthcare" 
              className="h-20 w-20 object-contain mb-3"
            />
            <h1 className="text-2xl font-bold text-[#4A238F]" style={{ fontFamily: 'Inter, sans-serif' }}>
              Zesta Healthcare
            </h1>
            <p className="text-xs text-[#4A238F]/70 tracking-wider mt-1 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
              Paperless Productive Practice
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="pl-10 bg-slate-50 border-slate-200"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                  className="pl-10 pr-10 bg-slate-50 border-slate-200"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="password-toggle-btn"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-right">
                <a 
                  href="/reset-password?type=hospital" 
                  className="text-sm text-[#4A238F] hover:underline"
                >
                  Forgot Password?
                </a>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-button"
              className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}